import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { ResponsiveOverlay } from '@/components/ResponsiveOverlay';
import AuthMethodsSection from '@/components/AccountLinking/AuthMethodsSection';
import SignInMethodsHelpLink from '@/components/AccountLinking/SignInMethodsHelpLink';
import AppVersionLabel from '@/components/AppVersionLabel/AppVersionLabel';
import SVGSpinner from '@/components/svgs/SVGSpinner';
import { useAuth } from '@/contexts';
import { unlinkAuthProvider } from '@/api/auth';
import { getOAuthSignInUrl, startOAuthSignIn } from '@/lib/cognito';
import { encodeOAuthState, getUtmsWithFallback } from '@/lib/utm';
import { isNativePlatform } from '@/lib/platform';
import { nativeConfirm } from '@/lib/nativeDialog';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { useAdvisorProStatus } from '@/hooks/useAdvisorProStatus';
import { useDeleteAccount } from '@/hooks/useDeleteAccount';
import { error } from '@/utils/logger';
import {
  isAuthMethodChangeError,
  oauthProviderName,
  providerLabel,
  readAuthSession,
  saveAccountLink,
  type AuthMethodsSummary,
} from '@/lib/accountLinking';
import {
  accountSettingsReturnPath,
  clearAccountSettingsOAuthPending,
  isRetriableAuthMethodsLoadError,
  loadAuthMethodsForAccountSettings,
  markAccountSettingsOAuthPending,
  type AccountSettingsLinkError,
} from '@/lib/accountSettings';
import {
  trackDeleteAccountStarted,
  trackSignInMethodBlocked,
  trackSignInMethodLinkStarted,
  trackSignInMethodRemoved,
  trackSignInMethodsViewed,
} from '@/lib/accountLinkAnalytics';
import SetPasswordStep, { type SetPasswordOutcome } from './SetPasswordStep';
import ChangePasswordStep from './ChangePasswordStep';
import DeleteAccountConfirmStep from './DeleteAccountConfirmStep';
import { linkErrorMessage, PASSWORD_CHANGED_MESSAGE, PASSWORD_SET_MESSAGE } from './accountSettingsCopy';

type Step = 'main' | 'set_password' | 'change_password' | 'delete_confirm';

/** A refresh often fires the methods request before the restored token is usable. */
const AUTO_LOAD_ATTEMPTS = 4;
const AUTO_LOAD_RETRY_MS = 700;
type SocialProvider = 'google' | 'apple';

interface Notice {
  tone: 'success' | 'error';
  text: string;
}

export interface AccountSettingsLinkFailure {
  code: AccountSettingsLinkError;
  provider: SocialProvider | null;
}

interface AccountSettingsModalProps {
  onClose: () => void;
  linkFailure?: AccountSettingsLinkFailure | null;
  /** Wait until AuthContext has finished restoring tokens before calling /auth/methods. */
  authReady?: boolean;
  /** True while returning from Hosted UI for a settings Connect. */
  oauthReturnPending?: boolean;
  onOAuthReturnSettled?: () => void;
  /** Fired once the merge/link error banner is committed. */
  onLinkFailureDisplayed?: () => void;
  /** Block backdrop, Escape, and the close button while a return sheet is settling. */
  preventClose?: boolean;
}

const DELETE_MESSAGE = "This permanently deletes your profile, trips, and lists. This can't be undone.";
const ADVISOR_PRO_NOTICE = 'Your Advisor Pro subscription will be canceled.';

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  onClose,
  linkFailure = null,
  authReady = true,
  oauthReturnPending = false,
  onOAuthReturnSettled,
  onLinkFailureDisplayed,
  preventClose = false,
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useIsMobileViewport();
  const isMobileSheetLayout = isMobileViewport || isNativePlatform();
  const { isDeleting, confirmDelete } = useDeleteAccount();
  const { state: advisorPro } = useAdvisorProStatus({ enabled: user?.profile_type === 'advisor' });
  const showAdvisorProNotice = advisorPro?.status === 'active' || advisorPro?.status === 'past_due';

  const [step, setStep] = useState<Step>('main');
  const [summary, setSummary] = useState<AuthMethodsSummary>({ methods: [], email: null });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [isOAuthRedirecting, setIsOAuthRedirecting] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState<SocialProvider | null>(null);
  const hasTrackedView = useRef(false);
  const loadGeneration = useRef(0);
  const showConnectSpinner = !linkFailure && (
    isOAuthRedirecting || (oauthReturnPending && (isLoading || !authReady))
  );
  const canDismiss = !isDeleting && !showConnectSpinner && !preventClose;

  const signInEmail = summary.email ?? user?.email ?? null;
  const showMergeFaqInNotice =
    linkFailure?.code === 'ACCOUNT_LINK_REQUIRED'
    || (notice?.tone === 'error' && notice.text.includes('separate Globe Thrivers account'));

  useLayoutEffect(() => {
    if (!linkFailure || step !== 'main') return;
    setNotice({ tone: 'error', text: linkErrorMessage(linkFailure.code, linkFailure.provider) });
    onLinkFailureDisplayed?.();
    onOAuthReturnSettled?.();
  }, [linkFailure, step, onLinkFailureDisplayed, onOAuthReturnSettled]);

  const oauthReturnPendingRef = useRef(oauthReturnPending);
  const linkFailureRef = useRef(linkFailure);
  const onOAuthReturnSettledRef = useRef(onOAuthReturnSettled);
  oauthReturnPendingRef.current = oauthReturnPending;
  linkFailureRef.current = linkFailure;
  onOAuthReturnSettledRef.current = onOAuthReturnSettled;

  const refresh = useCallback(async () => {
    if (!authReady) return;
    const generation = ++loadGeneration.current;
    setIsLoading(true);
    setLoadError('');
    try {
      for (let attempt = 0; attempt < AUTO_LOAD_ATTEMPTS; attempt += 1) {
        try {
          const loaded = await loadAuthMethodsForAccountSettings();
          if (generation !== loadGeneration.current) return;
          setSummary(loaded);
          if (!hasTrackedView.current) {
            trackSignInMethodsViewed(loaded.methods);
            hasTrackedView.current = true;
          }
          return;
        } catch (err) {
          if (generation !== loadGeneration.current) return;
          const tryAgain = isRetriableAuthMethodsLoadError(err) && attempt < AUTO_LOAD_ATTEMPTS - 1;
          if (!tryAgain) {
            error('[AccountSettingsModal] Failed to load sign-in methods', err);
            setLoadError('Sign-in methods could not be loaded.');
            return;
          }
          await new Promise((resolve) => {
            setTimeout(resolve, AUTO_LOAD_RETRY_MS);
          });
        }
      }
    } finally {
      if (generation === loadGeneration.current) {
        setIsLoading(false);
        if (oauthReturnPendingRef.current && !linkFailureRef.current) {
          onOAuthReturnSettledRef.current?.();
        }
      }
    }
  }, [authReady]);

  useEffect(() => {
    if (!authReady) {
      setIsLoading(true);
      setLoadError('');
      return;
    }
    void refresh();
  }, [authReady, refresh]);

  const connect = async (provider: SocialProvider): Promise<void> => {
    const session = readAuthSession();
    if (!session.accessToken || !session.idToken) {
      setNotice({ tone: 'error', text: 'Sign in again before connecting another method.' });
      return;
    }
    setNotice(null);
    trackSignInMethodLinkStarted(provider);
    const redirectPath = accountSettingsReturnPath(location.pathname, location.search);
    markAccountSettingsOAuthPending(redirectPath, provider);
    setConnectingProvider(provider);
    setIsOAuthRedirecting(true);
    saveAccountLink({
      kind: 'settings',
      provider,
      destinationAccessToken: session.accessToken,
      destinationIdToken: session.idToken,
      destinationRefreshToken: session.refreshToken,
      redirectPath,
    });
    const state = encodeOAuthState({
      utms: getUtmsWithFallback(),
      redirectPath,
    });
    try {
      await startOAuthSignIn(getOAuthSignInUrl(oauthProviderName(provider), state));
    } catch (err) {
      setIsOAuthRedirecting(false);
      setConnectingProvider(null);
      clearAccountSettingsOAuthPending();
      error('[AccountSettingsModal] Could not start OAuth for settings link', err);
      setNotice({ tone: 'error', text: linkErrorMessage('LINK_FAILED', provider) });
    }
  };

  const remove = async (provider: SocialProvider): Promise<void> => {
    setIsBusy(true);
    setNotice(null);
    try {
      await unlinkAuthProvider(provider);
      trackSignInMethodRemoved(provider, summary.methods.filter((method) => method !== provider));
      await refresh();
      setNotice({ tone: 'success', text: `${providerLabel(provider)} removed.` });
    } catch (err) {
      if (isAuthMethodChangeError(err)) {
        trackSignInMethodBlocked(err.code, provider);
        setNotice({
          tone: 'error',
          text: err.code === 'PASSWORD_REQUIRED'
            ? `Set a password before removing ${providerLabel(provider)}.`
            : 'Keep at least one sign-in method.',
        });
      } else {
        error('[AccountSettingsModal] Failed to remove method', err);
        setNotice({ tone: 'error', text: `Could not remove ${providerLabel(provider)}. Try again.` });
      }
    } finally {
      setIsBusy(false);
    }
  };

  const explainBlockedRemove = (provider: SocialProvider): void => {
    trackSignInMethodBlocked('PASSWORD_REQUIRED', provider);
    setNotice({
      tone: 'error',
      text: `Set a password first. You need one before you can remove ${providerLabel(provider)}.`,
    });
  };

  const handlePasswordSet = async (outcome: SetPasswordOutcome): Promise<void> => {
    if (outcome === 'signed_out') {
      toast.info('Password set. Sign in with your email and new password.');
      await logout();
      navigate('/login', { replace: true });
      return;
    }
    setStep('main');
    setNotice({
      tone: 'success',
      text: outcome === 'set' ? PASSWORD_SET_MESSAGE : 'This account already has a password.',
    });
    await refresh();
  };

  const startDelete = async (): Promise<void> => {
    trackDeleteAccountStarted();
    if (isNativePlatform()) {
      const confirmed = await nativeConfirm({
        title: 'Delete your account?',
        message: showAdvisorProNotice ? `${DELETE_MESSAGE}\n\n${ADVISOR_PRO_NOTICE}` : DELETE_MESSAGE,
        okButtonTitle: 'Delete',
        cancelButtonTitle: 'Cancel',
      });
      if (confirmed) await confirmDelete();
      return;
    }
    setNotice(null);
    setStep('delete_confirm');
  };

  const panelClassName = isMobileSheetLayout
    ? 'flex max-h-[92dvh] flex-col bg-white'
    : 'flex w-[min(560px,calc(100vw-32px))] flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_20px_60px_rgba(0,0,0,0.28)]';
  const gutterClassName = isMobileSheetLayout ? 'px-5' : 'px-8';

  return (
    <ResponsiveOverlay
      onClose={canDismiss ? onClose : undefined}
      presentation="sheet"
      forceMobileSheet={isMobileSheetLayout}
      keyboardAware={!isMobileSheetLayout}
      panelClassName={panelClassName}
      aria-label="Account Settings"
    >
      <div className={`flex shrink-0 items-center justify-between ${gutterClassName} ${isMobileSheetLayout ? 'pb-2 pt-2' : 'pb-2 pt-8'}`}>
        <h2 className="m-0 font-manrope text-[22px] font-extrabold text-[#1a1a1c]">Account Settings</h2>
        <button
          type="button"
          onClick={onClose}
          disabled={!canDismiss}
          aria-label="Close Account Settings"
          className="flex h-8 w-8 items-center justify-center rounded-2xl border-none bg-[#f4f4f5] text-[#252528]"
        >
          <CloseIcon sx={{ fontSize: 16 }} aria-hidden />
        </button>
      </div>

      <div className={`relative flex min-h-0 flex-1 flex-col ${gutterClassName}`}>
        {showConnectSpinner && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-[inherit] bg-white/90 px-6"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <SVGSpinner className="h-10 w-10" color="#252528" />
            <p className="m-0 text-center font-manrope text-sm font-medium text-[#525257]">
              {isOAuthRedirecting && connectingProvider
                ? `Connecting ${providerLabel(connectingProvider)}…`
                : 'Updating sign-in methods…'}
            </p>
          </div>
        )}
      <div className={`gt-keyboard-scroll flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pb-8 pt-4`}>
        {notice && step === 'main' && (
          <div
            role={notice.tone === 'error' ? 'alert' : 'status'}
            className={
              notice.tone === 'error'
                ? 'flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3.5'
                : 'flex items-center gap-3 rounded-2xl border border-[#e1e1e7] bg-white px-4 py-3.5 shadow-[0_8px_12px_rgba(0,0,0,0.05)]'
            }
          >
            {notice.tone === 'success' && (
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-2xl bg-[#f2f2f7] text-[#252528]">
                <CheckIcon sx={{ fontSize: 18 }} aria-hidden />
              </span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <p className={`m-0 font-manrope text-sm font-medium leading-5 tracking-[0.28px] ${notice.tone === 'error' ? 'text-red-600' : 'text-[#252528]'}`}>
                {notice.text}
              </p>
              {showMergeFaqInNotice && (
                <SignInMethodsHelpLink
                  onBeforeNavigate={onClose}
                  className="w-fit font-manrope text-sm font-bold text-[#1a1a1c] underline decoration-[#1a1a1c]/30 underline-offset-2"
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Dismiss message"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[14px] border-none bg-[#f2f2f7] text-[#252528]"
            >
              <CloseIcon sx={{ fontSize: 16 }} aria-hidden />
            </button>
          </div>
        )}

        {step === 'main' && isLoading && (
          <div
            className="flex min-h-[500px] items-center justify-center"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading sign-in methods"
          >
            {!showConnectSpinner && <SVGSpinner className="h-10 w-10" color="#252528" />}
          </div>
        )}
        {step === 'main' && !isLoading && (
          <>
            {loadError && (
              <div className="flex items-center justify-between gap-3">
                <p className="m-0 font-manrope text-sm text-red-600">{loadError}</p>
                <button
                  type="button"
                  onClick={() => { void refresh(); }}
                  className="border-none bg-transparent p-0 font-manrope text-sm font-bold text-[#1a1a1c]"
                >
                  Try again
                </button>
              </div>
            )}
            {!loadError && (
              <AuthMethodsSection
                methods={summary.methods}
                signInEmail={signInEmail}
                isBusy={isBusy}
                onSetPassword={() => { setNotice(null); setStep('set_password'); }}
                onChangePassword={() => { setNotice(null); setStep('change_password'); }}
                onConnect={(provider) => { void connect(provider); }}
                onRemove={(provider) => { void remove(provider); }}
                onRemoveBlocked={explainBlockedRemove}
                onSignInMethodsHelpNavigate={onClose}
              />
            )}
            <div className="h-px w-full shrink-0 bg-[#e1e1e7]" aria-hidden="true" />
            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => { void startDelete(); }}
                disabled={isDeleting}
                className="border-none bg-transparent p-0 font-manrope text-sm font-bold text-[#ff1a00] disabled:opacity-50"
              >
                Delete account
              </button>
              <AppVersionLabel />
            </div>
          </>
        )}

        {step === 'set_password' && (
          <SetPasswordStep
            signInEmail={signInEmail}
            onCancel={() => setStep('main')}
            onFinished={(outcome) => { void handlePasswordSet(outcome); }}
            onSignInMethodsHelpNavigate={onClose}
          />
        )}

        {step === 'change_password' && (
          <ChangePasswordStep
            signInEmail={signInEmail}
            onCancel={() => setStep('main')}
            onChanged={() => {
              setStep('main');
              setNotice({ tone: 'success', text: PASSWORD_CHANGED_MESSAGE });
            }}
          />
        )}

        {step === 'delete_confirm' && (
          <DeleteAccountConfirmStep
            showAdvisorProNotice={showAdvisorProNotice}
            isDeleting={isDeleting}
            onCancel={() => setStep('main')}
            onConfirm={() => { void confirmDelete(); }}
          />
        )}
      </div>
      </div>
    </ResponsiveOverlay>
  );
};

export default AccountSettingsModal;
